"use client";

import { c as _c } from "react/compiler-runtime";
import { FloatingPortal } from "@floating-ui/react";
import { autoUpdate, flip, size as floatingSize, offset, useFloating } from "@floating-ui/react-dom";
import { Label, Listbox, ListboxOptions, Transition } from "@headlessui/react";
import clsx from "clsx";
import React, { DetailedReactHTMLElement, HTMLAttributes, ReactElement, useId, useMemo } from "react";
import ErrorMessage from "../ErrorMessage";
import InfoIcon from "../InfoIcon";
import styles from "./Select.module.css";
import NativeSelect from "./NativeSelect";
import SelectButton from "./SelectButton";
import SelectOption from "./SelectOption";
import { SelectButtonProps, SelectOptionProps, SelectSize } from "./types";
import { findSelectOptionIndex, parseSelectOptions } from "./utils";
export interface SelectProps<T> extends Omit<HTMLAttributes<HTMLDivElement>, "defaultValue" | "onChange" | "prefix"> {
  onChange?: (value: T) => void;
  value?: T;
  className?: string;
  label?: string;
  children: ReactElement<SelectButtonProps> | ReactElement<SelectButtonProps>[] | any;
  displayValue?: (value: T) => string | null;
  disabled?: boolean;
  button?: ReactElement<SelectButtonProps>;
  size?: SelectSize;
  prefix?: ReactElement;
  labelHidden?: boolean;
  placeholder?: string;
  errorMessage?: string;
  defaultValue?: T;
  info?: string;
}

// Modal dialogs block pointer events outside themselves, so the options must open inside the dialog.
const dialogOf = (reference: object | null) => reference && "closest" in reference ? (reference as Element).closest<HTMLElement>("[role=dialog]") ?? undefined : undefined;
const Select = t0 => {
  const $ = _c(108);
  let children;
  let className;
  let defaultValue;
  let disabled;
  let displayValue;
  let errorMessage;
  let info;
  let label;
  let labelHidden;
  let onChange;
  let placeholder;
  let prefix;
  let props;
  let t1;
  let value;
  if ($[0] !== t0) {
    ({
      children,
      className,
      onChange,
      value,
      label,
      displayValue,
      disabled,
      size: t1,
      prefix,
      labelHidden,
      defaultValue,
      placeholder,
      errorMessage,
      info,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = defaultValue;
    $[4] = disabled;
    $[5] = displayValue;
    $[6] = errorMessage;
    $[7] = info;
    $[8] = label;
    $[9] = labelHidden;
    $[10] = onChange;
    $[11] = placeholder;
    $[12] = prefix;
    $[13] = props;
    $[14] = t1;
    $[15] = value;
  } else {
    children = $[1];
    className = $[2];
    defaultValue = $[3];
    disabled = $[4];
    displayValue = $[5];
    errorMessage = $[6];
    info = $[7];
    label = $[8];
    labelHidden = $[9];
    onChange = $[10];
    placeholder = $[11];
    prefix = $[12];
    props = $[13];
    t1 = $[14];
    value = $[15];
  }
  const size = t1 === undefined ? "md" : t1;
  const selectId = useId();
  const t2 = size === "md";
  const t3 = size === "sm";
  let t4;
  if ($[16] !== labelHidden || $[17] !== t2 || $[18] !== t3) {
    t4 = clsx("mb-xs flex", {
      "text-title-1": t2,
      "text-app-title-1": t3,
      "sr-only": labelHidden
    });
    $[16] = labelHidden;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const labelClassName = t4;
  let t5;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      placement: "bottom-start",
      strategy: "fixed",
      whileElementsMounted: autoUpdate,
      middleware: [offset(8), flip(), floatingSize({
        apply(t6) {
          const {
            availableHeight,
            elements,
            availableWidth
          } = t6;
          const rect = elements.reference.getBoundingClientRect();
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.min(availableHeight, 400) - 16}px`,
            maxWidth: `${availableWidth - 16}px`,
            minWidth: `${rect.width}px`
          });
        }
      })]
    };
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const {
    x,
    y,
    refs: t6,
    elements: t7,
    strategy
  } = useFloating(t5);
  const {
    setReference,
    setFloating
  } = t6;
  const {
    reference
  } = t7;
  let t8;
  if ($[21] !== children || $[22] !== size) {
    let t9;
    if ($[24] !== size) {
      t9 = child => React.cloneElement(child, {
        size
      } as SelectOptionProps<T>);
      $[24] = size;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    t8 = React.Children.map(children, t9);
    $[21] = children;
    $[22] = size;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const optionElements = t8;
  let t9;
  if ($[26] !== displayValue || $[27] !== optionElements) {
    t9 = parseSelectOptions(optionElements, displayValue);
    $[26] = displayValue;
    $[27] = optionElements;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const parsedOptions = t9;
  const hasValue = findSelectOptionIndex(parsedOptions, value) >= 0;
  let t10;
  if ($[29] !== reference) {
    t10 = dialogOf(reference);
    $[29] = reference;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const portalRoot = t10;
  let t11;
  bb0: {
    if (typeof value === "undefined") {
      t11 = placeholder || "";
      break bb0;
    }
    if (displayValue) {
      let t12;
      if ($[31] !== displayValue || $[32] !== value) {
        t12 = displayValue(value);
        $[31] = displayValue;
        $[32] = value;
        $[33] = t12;
      } else {
        t12 = $[33];
      }
      t11 = t12;
      break bb0;
    }
    let t12;
    if ($[34] !== optionElements || $[35] !== value) {
      let t13;
      if ($[37] !== value) {
        t13 = o => o.props.value === value;
        $[37] = value;
        $[38] = t13;
      } else {
        t13 = $[38];
      }
      t12 = optionElements.find(t13);
      $[34] = optionElements;
      $[35] = value;
      $[36] = t12;
    } else {
      t12 = $[36];
    }
    const option = t12;
    if (option) {
      t11 = option.props.children;
      break bb0;
    }
    t11 = String(value);
  }
  const buttonLabel = t11;
  const t12 = !!errorMessage;
  let t13;
  if ($[39] !== className) {
    t13 = clsx("relative flex", className);
    $[39] = className;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== info || $[42] !== label || $[43] !== labelClassName || $[44] !== selectId) {
    t14 = label && <label htmlFor={selectId} className={labelClassName}>{label}{info && <InfoIcon>{info}</InfoIcon>}</label>;
    $[41] = info;
    $[42] = label;
    $[43] = labelClassName;
    $[44] = selectId;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== disabled || $[47] !== onChange || $[48] !== parsedOptions || $[49] !== placeholder || $[50] !== prefix || $[51] !== selectId || $[52] !== size || $[53] !== value) {
    t15 = <NativeSelect id={selectId} options={parsedOptions} value={value} onChange={onChange} size={size} disabled={disabled} placeholder={placeholder} prefix={prefix} />;
    $[46] = disabled;
    $[47] = onChange;
    $[48] = parsedOptions;
    $[49] = placeholder;
    $[50] = prefix;
    $[51] = selectId;
    $[52] = size;
    $[53] = value;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== t14 || $[56] !== t15) {
    t16 = <div className={styles.nativeSelect}>{t14}{t15}</div>;
    $[55] = t14;
    $[56] = t15;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  const t17 = typeof onChange === "function" ? onChange : _temp;
  let t18;
  if ($[58] !== info || $[59] !== label || $[60] !== labelClassName) {
    t18 = label && <Label className={labelClassName}>{label}{info && <InfoIcon>{info}</InfoIcon>}</Label>;
    $[58] = info;
    $[59] = label;
    $[60] = labelClassName;
    $[61] = t18;
  } else {
    t18 = $[61];
  }
  const t19 = !hasValue;
  let t20;
  if ($[62] !== t19) {
    t20 = clsx("flex gap-xs flex-wrap items-center text-left", {
      "text-neutral-150": t19
    });
    $[62] = t19;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  let t21;
  if ($[64] !== buttonLabel || $[65] !== prefix || $[66] !== t20) {
    t21 = <div className={t20}>{prefix}{buttonLabel}</div>;
    $[64] = buttonLabel;
    $[65] = prefix;
    $[66] = t20;
    $[67] = t21;
  } else {
    t21 = $[67];
  }
  let t22;
  if ($[68] !== disabled || $[69] !== setReference || $[70] !== size || $[71] !== t21) {
    t22 = <SelectButton ref={setReference} size={size} disabled={disabled}>{t21}</SelectButton>;
    $[68] = disabled;
    $[69] = setReference;
    $[70] = size;
    $[71] = t21;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] !== t18 || $[74] !== t22) {
    t23 = <div className="w-full">{t18}{t22}</div>;
    $[73] = t18;
    $[74] = t22;
    $[75] = t23;
  } else {
    t23 = $[75];
  }
  const t24 = portalRoot ? "dialog" : "page";
  const t25 = y ?? 0;
  const t26 = x ?? 0;
  let t27;
  if ($[76] !== strategy || $[77] !== t25 || $[78] !== t26) {
    t27 = {
      position: strategy,
      top: t25,
      left: t26
    };
    $[76] = strategy;
    $[77] = t25;
    $[78] = t26;
    $[79] = t27;
  } else {
    t27 = $[79];
  }
  let t28;
  if ($[80] !== optionElements) {
    t28 = <ListboxOptions className="focus:outline-none overflow-auto w-full">{optionElements}</ListboxOptions>;
    $[80] = optionElements;
    $[81] = t28;
  } else {
    t28 = $[81];
  }
  let t29;
  if ($[82] !== setFloating || $[83] !== t27 || $[84] !== t28) {
    t29 = <Transition as="div" enter="transition ease-out duration-100" enterFrom="transform opacity-0" enterTo="transform opacity-100" leave="transition ease-in duration-75" leaveFrom="transform opacity-100" leaveTo="transform opacity-0" ref={setFloating} style={t27} className="z-[51] mt-2 w-fit rounded-md bg-neutral-0 focus:outline-none rounded-s shadow-3 py-2xs flex flex-col">{t28}</Transition>;
    $[82] = setFloating;
    $[83] = t27;
    $[84] = t28;
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  let t30;
  if ($[86] !== portalRoot || $[87] !== t24 || $[88] !== t29) {
    t30 = <FloatingPortal key={t24} root={portalRoot}>{t29}</FloatingPortal>;
    $[86] = portalRoot;
    $[87] = t24;
    $[88] = t29;
    $[89] = t30;
  } else {
    t30 = $[89];
  }
  let t31;
  if ($[90] !== defaultValue || $[91] !== disabled || $[92] !== t17 || $[93] !== t23 || $[94] !== t30 || $[95] !== value) {
    t31 = <div className={styles.styledSelect}><Listbox defaultValue={defaultValue} disabled={disabled} as="div" className="relative flex w-full" value={value} onChange={t17}>{t23}{t30}</Listbox></div>;
    $[90] = defaultValue;
    $[91] = disabled;
    $[92] = t17;
    $[93] = t23;
    $[94] = t30;
    $[95] = value;
    $[96] = t31;
  } else {
    t31 = $[96];
  }
  let t32;
  if ($[97] !== errorMessage) {
    t32 = errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>;
    $[97] = errorMessage;
    $[98] = t32;
  } else {
    t32 = $[98];
  }
  let t33;
  if ($[99] !== t16 || $[100] !== t31 || $[101] !== t32) {
    t33 = <div className="w-full">{t16}{t31}{t32}</div>;
    $[99] = t16;
    $[100] = t31;
    $[101] = t32;
    $[102] = t33;
  } else {
    t33 = $[102];
  }
  let t34;
  if ($[103] !== props || $[104] !== t12 || $[105] !== t13 || $[106] !== t33) {
    t34 = <div aria-invalid={t12} className={t13} {...props}>{t33}</div>;
    $[103] = props;
    $[104] = t12;
    $[105] = t13;
    $[106] = t33;
    $[107] = t34;
  } else {
    t34 = $[107];
  }
  return t34;
};
export default Object.assign(Select, {
  Option: SelectOption
});
function _temp() {}